import type {
  AccountGradingRecording,
  CustomerInteractionText,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { DateTime } from "luxon";

/** A call, or a run of texts between calls, in an interaction's transcript. */
export type GradingTranscriptItem =
  | { type: "call"; recording: AccountGradingRecording }
  | { type: "texts"; texts: CustomerInteractionText[] };

/**
 * An interaction's calls and texts in the order they happened, with each run
 * of texts between two calls kept together. Both arrive oldest first.
 */
export function buildGradingTranscriptItems(
  recordings: readonly AccountGradingRecording[],
  texts: readonly CustomerInteractionText[],
): GradingTranscriptItem[] {
  const items: GradingTranscriptItem[] = [];
  let callIndex = 0;

  for (const text of texts) {
    const textAt = DateTime.fromISO(text.at).toMillis();
    let nextCall = recordings[callIndex];
    while (
      nextCall &&
      DateTime.fromISO(nextCall.recorded_at).toMillis() <= textAt
    ) {
      items.push({ type: "call", recording: nextCall });
      callIndex += 1;
      nextCall = recordings[callIndex];
    }

    const lastItem = items.at(-1);
    if (lastItem?.type === "texts") {
      lastItem.texts.push(text);
    } else {
      items.push({ type: "texts", texts: [text] });
    }
  }

  for (const recording of recordings.slice(callIndex)) {
    items.push({ type: "call", recording });
  }

  return items;
}
